import { Injectable } from '@angular/core'
import {
	HttpRequest,
	HttpHandler,
	HttpEvent,
	HttpInterceptor,
	HttpResponse,
} from '@angular/common/http'
import { catchError, Observable, tap, throwError } from 'rxjs'
import { Store } from '@ngxs/store'
import { AppActions } from './store/app.actions'

// prettier-ignore
interface EmbraceHttpException {
	title: string;
	status: number;
	detail: string;
	errors: {
		[key: string]: any;
	};
}

@Injectable()
export class LoaderInterceptor implements HttpInterceptor {
	#calls: string[] = []
	constructor(private readonly store: Store) { }

	intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		const { url } = request
		return next.handle(request).pipe(
			tap((ev) => {
				if (
					url.toLowerCase().includes('embracepetinsurance.com/api') ||
					url.toLowerCase().includes('azurewebsites.net/api')
				) {
					if (!(ev instanceof HttpResponse)) {
						this.#calls.push(request.url)
						this.store.dispatch(new AppActions.SetLoading(!!this.#calls.length))
						this.store.dispatch(new AppActions.AddLoadingUrl(url))

						return
					}
					this.#calls.pop()
					this.store.dispatch(new AppActions.SetLoading(!!this.#calls.length))
					this.store.dispatch(new AppActions.RemoveLoadingUrl(url))
				}
			}),
			catchError((err) => {
				console.error(err)
				const errorMessage = this.#buildErrorMessage(err, url)
				this.#calls.length = 0
				this.store.dispatch([
					new AppActions.SetLoading(!!this.#calls.length),
					new AppActions.SetError(errorMessage),
					new AppActions.RemoveLoadingUrl(url),
				])

				return throwError(() => err)
			})
		)
	}

	#buildErrorMessage(err: any, url: string): string {
		if (this.#isNoResultSearchError(err, url)) {
			return 'No search results were found. Please update your search and try again.'
		}

		if (err?.error && Object.keys(err.error).includes('traceId')) {
			return err.message || 'An Unknown error occurred!'
		}

		return this.#parsedErrorMessage(err?.error ?? err)
	}

	#parsedErrorMessage(err: EmbraceHttpException | string | any): string {
		if (typeof err === 'string') {
			try {
				const parsedError = JSON.parse(err) as EmbraceHttpException
				return (
					parsedError.detail?.split(' :: ')?.[1] ??
					(<any>parsedError).message ??
					err
				)
			} catch {
				return err || 'An Unknown error occurred!'
			}
		}

		if (typeof err === 'object' && err) {
			const detailMessage = err.detail?.split(' :: ')?.[1]
			return detailMessage || err.message || 'An Unknown error occurred!'
		}

		return 'An Unknown error occurred!'
	}

	#isNoResultSearchError(err: any, url: string): boolean {
		const normalizedUrl = url.toLowerCase()
		const isSearchRequest =
			normalizedUrl.includes('/search/searchcontacts') ||
			normalizedUrl.includes('/search/searchcontactsbypolicy')
		if (!isSearchRequest) return false

		const errorTitle = `${err?.error?.title ?? ''}`.toLowerCase()
		const errorDetail = `${err?.error?.detail ?? ''}`.toLowerCase()
		const searchErrorText = `${err?.message ?? ''} ${JSON.stringify(err?.error ?? '')}`.toLowerCase()
		return (
			err?.status === 404 ||
			errorTitle === 'policydetails_validation_message' ||
			errorDetail.includes('you must specify at least one contact id') ||
			searchErrorText.includes('no result') ||
			searchErrorText.includes('no results') ||
			searchErrorText.includes('no match') ||
			searchErrorText.includes('no record') ||
			searchErrorText.includes('not found')
		)
	}
}
